import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import zoomSdk from "@zoom/appssdk";
import AppLayout from "@amzn/meridian/esm/components/app-layout";
import Box from "@amzn/meridian/esm/components/box";
import Masthead from "@amzn/meridian/esm/components/masthead";
import Theme from "@amzn/meridian/esm/components/theme";
import { 
  AIMS_NOT_AVAILABLE_MESSAGE, 
  AIMS_STATUS, 
  ROLES, 
  SUMMARIZE_STATES,
  TRANSCRIPTION_STATES,
} from "../../constants";
import Loading from "../common/Loading";
import AimsException from "../common/AimsException";
import AimsTimeoutModal from "../common/AimsTimeoutModal";
import AimsNotAvailable from "./default/AimsNotAvailable";
import Transcript from "../transcript/Transcript";
import LogManager from "../utils/LogManager";
import { AimsAvailabilityApiResponse } from "../../localtest/LocalhostTest";
import aimsTheme from "../../meridian-theme-aims.json";
import ProductFeedback from "../common/feedback/ProductFeedback";
import { FEEDBACK_PAGE } from "../common/constants";

// Sub-components
import Footer from "./Footer";
import SessionHeader from "./session/SessionHeader";
import ErrorModals from "./error/ErrorModals";
import DefaultView from "./default/DefaultView";
import SessionView from "./session/SessionView";
import RtmsLoading from "./default/RtmsLoading";
import RtmsRestartLoading from "./default/RtmsRestartLoading";
import RtmsRejected from "./default/RtmsRejected";
import Summaries from "./summaries/Summaries";
import SummaryDetail from "./summaries/SummaryDetail";
import Settings from "./settings/Settings";
import { VIEWS, TABS } from "./constants";

// Utils
import {
  initializeZoomSdk,
  handleAimsAvailabilityResult,
} from "./utils/inMeetingInitializer";
import {
  onEndSummarize,
  createRTMSChangeHandler,
} from "./utils/rtmsSessionUtil";

/**
 * BraveheartHome - Main container for the in-meeting assistant UI.
 * 
 * This component is responsible for:
 * - State management for the in-meeting assistant feature
 * - Routing to appropriate sub-views based on session state
 * - Rendering the footer navigation (End, Summaries, Settings)
 * 
 * Sub-components:
 * - DefaultView: Shared view for initial state and session ended (with/without Start button)
 * - SessionView: Active session with tabs (Assistant, Agenda, Action Items)
 * 
 * @see utils/inMeetingInitializer.js for SDK init and AIMS availability
 * @see utils/rtmsSessionUtil.js for RTMS session control
 */
const BraveheartHome = ({ setLastKnownContext, featureFlags }) => {
  const navigate = useNavigate();
  
  // Loading and context state
  const [isLoading, setIsLoading] = useState(true);
  const [runningContext, setRunningContext] = useState("inMeeting");
  
  // User and meeting state
  const [role, setRole] = useState(null);
  const [alias, setAlias] = useState(null);
  const [meetingUUID, setMeetingUUID] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [appContext, setAppContext] = useState(null);
  
  // AIMS status state
  const [aimsStatus, setAimsStatus] = useState(AIMS_STATUS.AVAILABLE);
  const [aimsNotAvailableMessage, setAimsNotAvailableMessage] = useState(AIMS_NOT_AVAILABLE_MESSAGE.DEFAULT);
  
  // Summarization state
  const [summarizeState, setSummarizeState] = useState(SUMMARIZE_STATES.DEFAULT);
  const [transcriptionState, setTranscriptionState] = useState(TRANSCRIPTION_STATES.DEFAULT);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(false);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(null);
  const [rtmsStreamId, setRtmsStreamId] = useState(null);
  const [hasGermanyAttendee, setHasGermanyAttendee] = useState(false);
  const [autoStartMode, setAutoStartMode] = useState(null);
  const [isAutostarting, setIsAutostarting] = useState(false);
  
  // Chatbot state
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  
  // UI state
  const [view, setView] = useState(VIEWS.MAIN); // MAIN | SUMMARIES | SUMMARY_DETAIL | SETTINGS
  const [selectedTab, setSelectedTab] = useState(TABS.ASSISTANT);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [isServerErrorModalOpen, setIsServerErrorModalOpen] = useState(false);
  const [isRtmsErrorModalOpen, setIsRtmsErrorModalOpen] = useState(false);
  const [rtmsErrorType, setRtmsErrorType] = useState(null);
  const [summarizeClickCount, setSummarizeClickCount] = useState(0);
  const [isProductFeedbackModalOpen, setIsProductFeedbackModalOpen] = useState(false);
  // TODO: Used by checkForSummary when summary is ready after RTMS stops - will be needed for "View Summary" button
  const [aimsUuid, setAimsUuid] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  
  // Refs for state tracking in callbacks (from Home.jsx)
  const summarizeStateRef = useRef(summarizeState);
  const isRtmsStartedByOtherRef = useRef(isRtmsStartedByOther);
  
  // Refs for RTMS session control (from SummarizeBox.jsx)
  const rtmsStartTimestampRef = useRef(null);
  const hideStoppedComponentTimerRef = useRef(null);
  const sessionStatusIntervalRef = useRef(null);
  const sessionStatusTimeoutRef = useRef(null);
  const rejectionTimeoutRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    summarizeStateRef.current = summarizeState;
    if (summarizeState !== SUMMARIZE_STATES.LOADING) {
      isEndingRef.current = false;
      if (isRestarting) setIsRestarting(false);
    }
    if (summarizeState === SUMMARIZE_STATES.SUMMARIZING) {
      setChatMessages([]);
      setIsChatLoading(false);
    }
  }, [summarizeState]);

  useEffect(() => {
    isRtmsStartedByOtherRef.current = isRtmsStartedByOther;
    if (isRtmsStartedByOther && rtmsRequesterName) {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [isRtmsStartedByOther, rtmsRequesterName]);

  // Auto-dismiss banner after 1 minute
  useEffect(() => {
    if (!showBanner) return;
    const timer = setTimeout(() => {
      setShowBanner(false);
    }, 60000);
    return () => clearTimeout(timer);
  }, [showBanner]);

  // Set up RTMS status change listener
  useEffect(() => {
    if (window.location.href !== "http://localhost:3000/" && meetingUUID && !isLoading) {
      try {
        LogManager.info(`Setting up RTMS status change listener for meeting: ${meetingUUID}`);
        const handleRTMSChange = createRTMSChangeHandler({
          meetingUUID,
          alias,
          setSummarizeState,
          setIsRtmsStartedByOther,
          setRtmsRequesterName,
          setRtmsStreamId,
          setRtmsErrorType,
          setIsRtmsErrorModalOpen,
          setIsProductFeedbackModalOpen,
          setAimsUuid,
          setSummaryData,
          summarizeStateRef,
          isRtmsStartedByOtherRef,
          rtmsStartTimestampRef,
          hideStoppedComponentTimerRef,
        });
        zoomSdk.onRTMSStatusChange(handleRTMSChange);
      } catch (error) {
        LogManager.error("Error setting up RTMS status listener:", error);
      }
    }
    
    return () => {
      if (hideStoppedComponentTimerRef.current) clearTimeout(hideStoppedComponentTimerRef.current);
      if (sessionStatusIntervalRef.current) clearInterval(sessionStatusIntervalRef.current);
      if (sessionStatusTimeoutRef.current) clearTimeout(sessionStatusTimeoutRef.current);
      if (rejectionTimeoutRef.current) clearTimeout(rejectionTimeoutRef.current);
    };
  }, [meetingUUID, isLoading]);

  // Main effect: configure SDK and fetch AIMS status
  useEffect(() => {
    // Localhost testing override
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Running in localhost mode.");
      const availabilityResult = handleAimsAvailabilityResult(AimsAvailabilityApiResponse);
      setRole(availabilityResult.role);
      setAlias(availabilityResult.alias);
      // Override for testing - change these to test different views
      setRole(ROLES.ORGANIZER);
      setRunningContext("inMeeting");
      setMeetingUUID("testUUID");
      setAimsStatus(AIMS_STATUS.AVAILABLE);
      setSummarizeState(SUMMARIZE_STATES.DEFAULT);
      setRtmsRequesterName("John Doe");
      setIsRtmsStartedByOther(false);
      setIsLoading(false);
    } else {
      initializeZoomSdk({
        setRunningContext,
        setLastKnownContext,
        setMeetingUUID,
        setMeetingId,
        setRole,
        setAlias,
        setAimsStatus,
        setAimsNotAvailableMessage,
        setHasGermanyAttendee,
        setAutoStartMode,
        setAppContext,
        setSummarizeState,
        setTranscriptionState,
        setIsRtmsStartedByOther,
        setRtmsRequesterName,
        setRtmsStreamId,
        setIsAutostarting,
        setIsLoading,
        rtmsStartTimestampRef,
      });
    }
  }, []);

  // Redirect to /client when inMainClient is detected
  useEffect(() => {
    if (runningContext === "inMainClient") {
      LogManager.info("Detected inMainClient context, redirecting to /client.");
      navigate("/client");
    }
  }, [runningContext, navigate]);

  // Bottom navigation handlers
  const isEndingRef = useRef(false);

  const handleEndClick = () => {
    isEndingRef.current = true;
    setView(VIEWS.MAIN);
    onEndSummarize({
      summarizeState,
      setSummarizeState,
      setIsServerErrorModalOpen,
      meetingUUID,
      meetingId,
      role,
      appContext,
    });
  };

  const handleSummariesClick = () => {
    if (view === VIEWS.SUMMARIES || view === VIEWS.SUMMARY_DETAIL) {
      setView(VIEWS.MAIN);
      setSelectedSummary(null);
    } else {
      setView(VIEWS.SUMMARIES);
    }
  };

  const handleSelectSummary = (summary) => {
    setSelectedSummary(summary);
    setView(VIEWS.SUMMARY_DETAIL);
  };

  const handleBackToSummaries = () => {
    setSelectedSummary(null);
    setView(VIEWS.SUMMARIES);
  };

  const handleSettingsClick = () => {
    setView(view === VIEWS.SETTINGS ? VIEWS.MAIN : VIEWS.SETTINGS);
  };

  const handleTabClick = (tabId) => () => {
    setSelectedTab(tabId);
    setView(VIEWS.MAIN);
  };

  // Loading state
  if (isLoading || runningContext === "inMainClient") {
    return <Loading />;
  }

  // Error states
  if (aimsStatus === AIMS_STATUS.TIMEOUT) {
    return <AimsTimeoutModal isOpen={true} />;
  }

  if (aimsStatus === AIMS_STATUS.EXCEPTION) {
    return <AimsException />;
  }

  const isNotAvailable = aimsStatus === AIMS_STATUS.NOT_AVAILABLE;

  // Check if this is a transcript-only scenario (no tabs needed)
  const isTranscriptMode = aimsStatus === AIMS_STATUS.TRANSCRIPT_AVAILABLE || aimsStatus === AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE;

  // If transcript mode, render the Transcript component without tabs
  if (isTranscriptMode) {
    return (
      <Transcript
        aimsStatus={aimsStatus}
        role={role}
        meetingId={meetingId}
        meetingUuid={meetingUUID}
        alias={alias}
        appContext={appContext}
        initialIsRtmsStartedByOther={isRtmsStartedByOther}
        initialRtmsRequesterName={rtmsRequesterName}
        transcriptionState={transcriptionState}
        setTranscriptionState={setTranscriptionState}
        isRestarting={isRestarting}
        setIsRestarting={setIsRestarting}
        isAutostarting={isAutostarting}
        setIsAutostarting={setIsAutostarting}
        autoStartMode={autoStartMode}
      />
    );
  }

  // Check if End button should be visible (only for Organizer and Alternative Host when summarizing, and they started it)
  const showEndButton = (role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST) && 
                        summarizeState === SUMMARIZE_STATES.SUMMARIZING &&
                        !isRtmsStartedByOther;

  // Check if session is active (summarizing)
  const isSessionActive = summarizeState === SUMMARIZE_STATES.SUMMARIZING;

  // Check if RTMS is loading (starting or stopping)
  const isRtmsLoading = summarizeState === SUMMARIZE_STATES.LOADING;

  // Check if user can start summarization (organizer or alternative host without outlook warning restriction)
  const canStartSummarization = role === ROLES.ORGANIZER || 
    (role === ROLES.ALTERNATIVE_HOST && aimsStatus !== AIMS_STATUS.SUMMARY_TO_ORGANIZER);

  return (
    <Theme palette={aimsTheme}>
      <AppLayout
        headerComponent={Masthead}
        footerComponent={(isRtmsLoading && !isRestarting) ? undefined : Footer}
        backgroundColor="primary"
      >
        {/* Session Tabs Header - only visible during active session */}
        {isSessionActive && (
          <SessionHeader
            view={view}
            selectedTab={selectedTab}
            onTabClick={handleTabClick}
          />
        )}

        {/* Content Area */}
        <Box width="362px">
          {view === VIEWS.SUMMARIES ? (
            <Summaries onSelectSummary={handleSelectSummary} isSessionActive={isSessionActive} />
          ) : view === VIEWS.SUMMARY_DETAIL ? (
            <SummaryDetail summary={selectedSummary} onBack={handleBackToSummaries} isSessionActive={isSessionActive} />
          ) : view === VIEWS.SETTINGS ? (
            <Settings isVerboseSummariesEnabled={featureFlags?.isVerboseSummariesEnabled} />
          ) : isNotAvailable ? (
            <AimsNotAvailable aimsNotAvailableMessage={aimsNotAvailableMessage} />
          ) : summarizeState === SUMMARIZE_STATES.REJECTED ? (
            <RtmsRejected rtmsRequesterName={rtmsRequesterName} />
          ) : isRtmsLoading ? (
            isEndingRef.current ? <Loading /> : isRestarting ? <RtmsRestartLoading /> : <RtmsLoading />
          ) : isSessionActive ? (
            <SessionView
              selectedTab={selectedTab}
              meetingUuid={meetingUUID}
              meetingId={meetingId}
              rtmsStreamId={rtmsStreamId}
              rtmsStartTime={rtmsStartTimestampRef.current}
              alias={alias}
              role={role}
              rtmsRequesterName={rtmsRequesterName}
              isRtmsStartedByOther={isRtmsStartedByOther}
              showBanner={showBanner}
              setShowBanner={setShowBanner}
              chatMessages={chatMessages}
              setChatMessages={setChatMessages}
              isChatLoading={isChatLoading}
              setIsChatLoading={setIsChatLoading}
            />
          ) : (
            <DefaultView
              summarizeState={summarizeState}
              setSummarizeState={setSummarizeState}
              summarizeClickCount={summarizeClickCount}
              setSummarizeClickCount={setSummarizeClickCount}
              setRtmsRequesterName={setRtmsRequesterName}
              setRtmsStreamId={setRtmsStreamId}
              setRtmsErrorType={setRtmsErrorType}
              setIsRtmsErrorModalOpen={setIsRtmsErrorModalOpen}
              setIsServerErrorModalOpen={setIsServerErrorModalOpen}
              meetingUUID={meetingUUID}
              meetingId={meetingId}
              role={role}
              appContext={appContext}
              sessionStatusIntervalRef={sessionStatusIntervalRef}
              sessionStatusTimeoutRef={sessionStatusTimeoutRef}
              rejectionTimeoutRef={rejectionTimeoutRef}
              showStartButton={canStartSummarization}
            />
          )}
        </Box>
        {(!isRtmsLoading || isRestarting) && (
          <Footer
            showEndButton={showEndButton}
            onEndClick={handleEndClick}
            onSummariesClick={handleSummariesClick}
            onSettingsClick={handleSettingsClick}
            activeView={view}
          />
        )}
      </AppLayout>
      {/* Product Feedback Popover - fixed to bottom */}
      <div style={{ position: "fixed", bottom: 0, left: 0, width: "362px" }}>
        <ProductFeedback
          isModalOpen={isProductFeedbackModalOpen}
          setIsModalOpen={setIsProductFeedbackModalOpen}
          usePopover={true}
          page={FEEDBACK_PAGE.SUMMARIZE_END}
        />
      </div>
      {/* Error Modals */}
      <ErrorModals
        isServerErrorModalOpen={isServerErrorModalOpen}
        setIsServerErrorModalOpen={setIsServerErrorModalOpen}
        isRtmsErrorModalOpen={isRtmsErrorModalOpen}
        setIsRtmsErrorModalOpen={setIsRtmsErrorModalOpen}
        rtmsErrorType={rtmsErrorType}
        setRtmsErrorType={setRtmsErrorType}
        setSummarizeState={setSummarizeState}
        setSummarizeClickCount={setSummarizeClickCount}
        setIsRestarting={setIsRestarting}
        setIsRtmsStartedByOther={setIsRtmsStartedByOther}
        setRtmsRequesterName={setRtmsRequesterName}
        setRtmsStreamId={setRtmsStreamId}
        meetingUUID={meetingUUID}
        meetingId={meetingId}
        role={role}
        appContext={appContext}
        sessionStatusIntervalRef={sessionStatusIntervalRef}
        sessionStatusTimeoutRef={sessionStatusTimeoutRef}
        rejectionTimeoutRef={rejectionTimeoutRef}
      />
    </Theme>
  );
};

export default BraveheartHome;
